import { useEffect } from 'react';
import type { SceneStore } from '@os/doc';
import type { LinkServices } from './services';

/**
 * Resolves link cards still `pending` and embeds whose `embeddable` is unknown, once per object and session, on an
 * editor's client (any editor may do it: the first answer wins, the others repeat it). Returns a stop function.
 */
export function watchUnfurls(store: SceneStore, services: LinkServices): () => void {
  const done = new Set<string>();
  const run = () => {
    if (store.readOnly) return;
    for (const o of store.listAll()) {
      const wanted = (o.type === 'link' && o.status === 'pending') || (o.type === 'embed' && o.embeddable === null);
      if (!wanted || done.has(o.id)) continue;
      done.add(o.id);
      void (async () => {
        try {
          const r = await services.unfurl(o.url);
          const image = r.kind === 'link' ? r.image : r.thumbnail;
          if (r.kind === 'link') store.commands.setLinkPreview(o.id, { title: r.title, description: r.description, siteName: r.siteName, status: 'ready' });
          else store.commands.setLinkPreview(o.id, { title: r.title, embeddable: r.embeddable });
          const asset = image ? await services.thumbnail(image).catch(() => null) : null;
          if (asset) store.commands.setLinkPreview(o.id, { thumbAssetId: asset });
        } catch {
          if (o.type === 'link') store.commands.setLinkPreview(o.id, { status: 'failed' });
        }
      })();
    }
  };
  run();
  const offList = store.subscribeList(run);
  const offRo = store.subscribeReadOnly(run);
  return () => {
    offList();
    offRo();
  };
}

export function useUnfurl(store: SceneStore, services: LinkServices | null | undefined): void {
  useEffect(() => (services ? watchUnfurls(store, services) : undefined), [store, services]);
}
